/**
 * Shared theme and styles for SweetAlert2 modals (AlertConfirm, AlertRequest, AlertSave).
 * Reads data-mui-color-scheme + SATOS theme CSS variables from the document root.
 */

export type AlertColorScheme = "light" | "dark";

export interface AlertTheme {
	scheme: AlertColorScheme;
	background: string;
	color: string;
	/** Primary action (confirm / save) */
	confirmColor: string;
	/** Secondary cancel */
	cancelColor: string;
	/** Deny / destructive secondary */
	denyColor: string;
	/** Error / destructive primary (e.g. delete confirm) */
	errorColor: string;
	/** Border and divider */
	border: string;
	/** Subtext */
	subtext: string;
}

type AlertCustomClass = {
	popup?: string;
	container?: string;
	title?: string;
	htmlContainer?: string;
	actions?: string;
	confirmButton?: string;
	denyButton?: string;
	cancelButton?: string;
	closeButton?: string;
	icon?: string;
};

type AlertOptionsWithTheme = Record<string, unknown> & {
	customClass?: AlertCustomClass;
	didOpen?: (popup: HTMLElement) => void;
	backdrop?: string | boolean;
};

const ALERT_THEMES: Record<AlertColorScheme, Omit<AlertTheme, "scheme">> = {
	light: {
		background: "#FFFFFF",
		color: "#0F172A",
		subtext: "#52637A",
		confirmColor: "#4CAF50",
		cancelColor: "#64748B",
		denyColor: "#D61202",
		errorColor: "#D61202",
		border: "rgba(199, 210, 224, 0.8)",
	},
	dark: {
		background: "#0F172A",
		color: "#E6EDF7",
		subtext: "#9FB0C7",
		confirmColor: "#4CAF50",
		cancelColor: "#94A3B8",
		denyColor: "#E95E4D",
		errorColor: "#E95E4D",
		border: "rgba(100, 116, 139, 0.42)",
	},
};

function getRootStyles(): CSSStyleDeclaration | null {
	if (typeof document === "undefined") return null;
	return window.getComputedStyle(document.documentElement);
}

export function readThemeCssVar(name: string, fallback: string): string {
	const styles = getRootStyles();
	if (!styles) return fallback;
	const value = styles.getPropertyValue(name).trim();
	return value || fallback;
}

export function getAlertColorScheme(): AlertColorScheme {
	if (typeof document === "undefined") return "light";
	const scheme = document.documentElement.getAttribute("data-mui-color-scheme");
	return scheme === "dark" ? "dark" : "light";
}

export function getAlertTheme(): AlertTheme {
	const scheme = getAlertColorScheme();
	const fallback = ALERT_THEMES[scheme];
	return {
		scheme,
		background: readThemeCssVar(
			"--satos-palette-background-paper",
			fallback.background,
		),
		color: readThemeCssVar("--satos-palette-text-primary", fallback.color),
		subtext: readThemeCssVar(
			"--satos-palette-text-secondary",
			fallback.subtext,
		),
		confirmColor: readThemeCssVar(
			"--satos-palette-success",
			fallback.confirmColor,
		),
		cancelColor: readThemeCssVar(
			"--satos-palette-text-secondary",
			fallback.cancelColor,
		),
		denyColor: readThemeCssVar("--satos-palette-error", fallback.denyColor),
		errorColor: readThemeCssVar("--satos-palette-error", fallback.errorColor),
		border: readThemeCssVar("--satos-palette-divider", fallback.border),
	};
}

const STYLE_ID = "satos-alert-styles";

export function ensureAlertStyles(): void {
	if (typeof document === "undefined") return;
	if (document.getElementById(STYLE_ID)) return;
	const style = document.createElement("style");
	style.id = STYLE_ID;
	style.textContent = `
		.satos-alert-container {
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
			padding: clamp(16px, 4vw, 32px) !important;
		}
		.satos-alert-show {
			animation: satos-alert-show 180ms cubic-bezier(0.2, 0.9, 0.25, 1) both;
		}
		.satos-alert-hide {
			animation: satos-alert-hide 140ms ease-in both;
		}
		@keyframes satos-alert-show {
			from {
				opacity: 0;
				transform: translate3d(0, -10px, 0) scale(0.98);
			}
			to {
				opacity: 1;
				transform: translate3d(0, 0, 0) scale(1);
			}
		}
		@keyframes satos-alert-hide {
			from {
				opacity: 1;
				transform: translate3d(0, 0, 0) scale(1);
			}
			to {
				opacity: 0;
				transform: translate3d(0, -6px, 0) scale(0.985);
			}
		}
		.satos-alert {
			width: min(92vw, 500px) !important;
			border-radius: var(--satos-alert-radius, 10px) !important;
			box-shadow: var(--satos-alert-shadow, 0 22px 70px rgba(15, 23, 42, 0.18)) !important;
			border: var(--satos-alert-border-width, 1px) solid var(--satos-alert-border, rgba(0, 0, 0, 0.12)) !important;
			padding: 0 !important;
			overflow: hidden !important;
			font-family: inherit;
			transform-origin: top center;
		}
		.satos-alert .swal2-icon {
			margin: 18px auto 0 !important;
			transform: scale(0.84);
		}
		.satos-alert .swal2-title {
			font-size: 16px !important;
			font-weight: 600 !important;
			line-height: 1.4 !important;
			color: var(--satos-alert-color) !important;
			padding: 14px 16px 8px !important;
			margin: 0 !important;
		}
		.satos-alert .swal2-html-container {
			font-size: 14px !important;
			line-height: 1.5 !important;
			color: var(--satos-alert-subtext) !important;
			padding: 0 16px 0 !important;
			margin: 0 0 16px !important;
		}
		.satos-alert__actions {
			gap: 8px !important;
			flex-wrap: wrap !important;
			width: 100% !important;
			margin: 0 !important;
			padding: 12px 16px !important;
			background: var(--satos-alert-footer-bg) !important;
			border-top: var(--satos-alert-border-width, 1px) solid var(--satos-alert-border, rgba(0, 0, 0, 0.12)) !important;
			justify-content: flex-end !important;
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
		}
		.satos-alert .swal2-styled {
			border-radius: var(--satos-alert-control-radius, 8px) !important;
			font-weight: 600 !important;
			font-size: 14px !important;
			line-height: 1.2 !important;
			padding: 9px 14px !important;
			margin: 0 !important;
			min-height: 38px !important;
			box-shadow: none !important;
			border: 1px solid transparent !important;
			transition: background-color 120ms ease, border-color 120ms ease, filter 120ms ease !important;
		}
		.satos-alert .swal2-styled:hover {
			filter: brightness(1.02);
		}
		.satos-alert .swal2-styled:focus-visible {
			outline: none !important;
			box-shadow: 0 0 0 3px var(--satos-alert-focus-ring) !important;
		}
		.satos-alert__confirm,
		.satos-alert__deny {
			color: #ffffff !important;
		}
		.satos-alert__cancel {
			background: transparent !important;
			border-color: var(--satos-alert-field-border) !important;
			color: var(--satos-alert-cancel-text, var(--satos-alert-subtext)) !important;
		}
		.satos-alert__cancel:hover {
			background: var(--satos-alert-field-bg) !important;
		}
		.satos-alert .swal2-close {
			color: var(--satos-alert-subtext) !important;
			font-size: 22px !important;
			margin: 8px 8px 0 0 !important;
		}
		.satos-alert .swal2-close:hover {
			color: var(--satos-alert-color) !important;
			background: transparent !important;
		}
		.satos-alert .swal2-input-label {
			width: calc(100% - 32px) !important;
			margin: 0 auto 8px !important;
			text-align: left !important;
			font-size: 13px !important;
			font-weight: 600 !important;
			color: var(--satos-alert-subtext) !important;
		}
		.satos-alert .swal2-input,
		.satos-alert .swal2-textarea,
		.satos-alert .swal2-select,
		.satos-alert .swal2-file {
			width: calc(100% - 32px) !important;
			margin: 0 auto 16px !important;
			border-radius: var(--satos-alert-control-radius, 8px) !important;
			border: 1px solid var(--satos-alert-field-border) !important;
			background: var(--satos-alert-field-bg) !important;
			color: var(--satos-alert-color) !important;
			box-shadow: none !important;
			padding: 10px 12px !important;
			font-size: 14px !important;
			line-height: 1.4 !important;
			transition: border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease !important;
		}
		.satos-alert .swal2-input::placeholder,
		.satos-alert .swal2-textarea::placeholder {
			color: var(--satos-alert-subtext) !important;
			opacity: 0.9;
		}
		.satos-alert .swal2-input:hover,
		.satos-alert .swal2-textarea:hover,
		.satos-alert .swal2-select:hover,
		.satos-alert .swal2-file:hover {
			border-color: var(--satos-alert-field-border-hover) !important;
		}
		.satos-alert .swal2-input:focus,
		.satos-alert .swal2-textarea:focus,
		.satos-alert .swal2-select:focus,
		.satos-alert .swal2-file:focus {
			border-color: var(--satos-alert-focus-color) !important;
			box-shadow: 0 0 0 4px var(--satos-alert-focus-ring) !important;
		}
		.satos-alert .swal2-select option {
			color: var(--satos-alert-color);
		}
		.satos-alert .swal2-checkbox {
			width: calc(100% - 32px) !important;
			margin: 0 auto 16px !important;
			align-items: center !important;
			justify-content: flex-start !important;
			gap: 10px !important;
			color: var(--satos-alert-subtext) !important;
			font-size: 13px !important;
		}
		.satos-alert .swal2-checkbox input {
			accent-color: var(--satos-alert-focus-color);
		}
		.satos-alert .swal2-footer {
			width: 100% !important;
			margin: 0 !important;
			padding: 12px 16px !important;
			border-top: var(--satos-alert-border-width, 1px) solid var(--satos-alert-border, rgba(0, 0, 0, 0.12)) !important;
			background: var(--satos-alert-footer-bg) !important;
			color: var(--satos-alert-subtext) !important;
			font-size: 13px !important;
			line-height: 1.5 !important;
		}
		.satos-alert .swal2-validation-message {
			margin: 0 16px 16px !important;
			border-radius: var(--satos-alert-control-radius, 8px) !important;
			padding: 10px 12px !important;
			font-size: 13px !important;
			line-height: 1.4 !important;
			background: var(--satos-alert-validation-bg, rgba(254, 226, 226, 0.9)) !important;
			color: var(--satos-alert-validation-color, #991b1b) !important;
		}
		.satos-alert .swal2-loader {
			border-color: var(--satos-alert-focus-color) transparent var(--satos-alert-focus-color) transparent !important;
		}
		@media (max-width: 640px) {
			.satos-alert {
				width: min(100vw - 20px, 500px) !important;
			}
			.satos-alert__actions {
				padding: 16px !important;
				gap: 10px !important;
			}
			.satos-alert .swal2-styled {
				flex: 1 1 100% !important;
				width: 100% !important;
			}
			.satos-alert .swal2-input,
			.satos-alert .swal2-textarea,
			.satos-alert .swal2-select,
			.satos-alert .swal2-file,
			.satos-alert .swal2-input-label,
			.satos-alert .swal2-checkbox {
				width: calc(100% - 32px) !important;
			}
		}
	`;
	document.head.appendChild(style);
}

function joinClassNames(...classNames: Array<string | undefined>): string {
	return classNames.filter(Boolean).join(" ");
}

/** Apply theme to a Swal options object (background, color, customClass). Call didOpen to set CSS vars if needed. */
export function withAlertTheme<T extends AlertOptionsWithTheme>(
	opts: T,
	theme: AlertTheme,
): T & {
	background: string;
	color: string;
	backdrop: string | boolean;
	showClass: { popup: string };
	hideClass: { popup: string };
	customClass: AlertCustomClass;
	didOpen?: (popup: HTMLElement) => void;
} {
	ensureAlertStyles();
	const popupClass = `satos-alert satos-alert--${theme.scheme}`;
	const previousDidOpen = opts.didOpen;
	return {
		...opts,
		background: theme.background,
		color: theme.color,
		backdrop:
			opts.backdrop ??
			readThemeCssVar(
				"--satos-alert-backdrop",
				theme.scheme === "dark"
					? "rgba(3, 7, 18, 0.8)"
					: "rgba(15, 23, 42, 0.22)",
			),
		showClass: { popup: "satos-alert-show" },
		hideClass: { popup: "satos-alert-hide" },
		customClass: {
			...opts.customClass,
			container: joinClassNames(
				opts.customClass?.container,
				"satos-alert-container",
			),
			popup: joinClassNames(opts.customClass?.popup, popupClass),
			actions: joinClassNames(
				opts.customClass?.actions,
				"satos-alert__actions",
			),
			confirmButton: joinClassNames(
				opts.customClass?.confirmButton,
				"satos-alert__confirm",
			),
			denyButton: joinClassNames(
				opts.customClass?.denyButton,
				"satos-alert__deny",
			),
			cancelButton: joinClassNames(
				opts.customClass?.cancelButton,
				"satos-alert__cancel",
			),
		},
		didOpen: (popup: HTMLElement) => {
			const fieldBackground = readThemeCssVar(
				"--satos-alert-field-bg",
				theme.scheme === "dark" ? "rgba(15, 23, 42, 0.62)" : "#F8FAFC",
			);
			const fieldBorder = readThemeCssVar(
				"--satos-alert-field-border",
				theme.scheme === "dark"
					? "rgba(100, 116, 139, 0.62)"
					: "rgba(148, 163, 184, 0.65)",
			);
			const fieldBorderHover = readThemeCssVar(
				"--satos-alert-field-border-hover",
				theme.scheme === "dark"
					? "rgba(159, 176, 199, 0.54)"
					: "rgba(82, 99, 122, 0.42)",
			);
			const focusColor = readThemeCssVar(
				"--satos-alert-focus-color",
				theme.scheme === "dark" ? "#60A5FA" : "#1F80FF",
			);
			const focusRing = readThemeCssVar(
				"--satos-alert-focus-ring",
				theme.scheme === "dark"
					? "rgba(31, 128, 255, 0.24)"
					: "rgba(31, 128, 255, 0.16)",
			);
			const footerBackground = readThemeCssVar(
				"--satos-alert-footer-bg",
				theme.scheme === "dark"
					? "rgba(11, 18, 32, 0.52)"
					: "rgba(243, 247, 251, 0.76)",
			);
			const validationBackground = readThemeCssVar(
				"--satos-alert-validation-bg",
				theme.scheme === "dark"
					? "rgba(185, 28, 28, 0.24)"
					: "rgba(254, 226, 226, 0.9)",
			);
			const validationColor = readThemeCssVar(
				"--satos-alert-validation-color",
				theme.scheme === "dark" ? "#FECACA" : "#991B1B",
			);
			popup.style.setProperty("--satos-alert-color", theme.color);
			popup.style.setProperty("--satos-alert-subtext", theme.subtext);
			popup.style.setProperty("--satos-alert-border", theme.border);
			popup.style.setProperty(
				"--satos-alert-radius",
				readThemeCssVar(
					"--satos-surface-radius",
					theme.scheme === "dark" ? "12px" : "10px",
				),
			);
			popup.style.setProperty(
				"--satos-alert-control-radius",
				readThemeCssVar("--satos-control-radius", "8px"),
			);
			popup.style.setProperty(
				"--satos-alert-border-width",
				readThemeCssVar("--satos-surface-border-width", "1px"),
			);
			popup.style.setProperty(
				"--satos-alert-shadow",
				readThemeCssVar(
					"--satos-alert-shadow",
					theme.scheme === "dark"
						? "0 26px 90px rgba(0, 0, 0, 0.48)"
						: "0 22px 70px rgba(15, 23, 42, 0.18)",
				),
			);
			popup.style.setProperty("--satos-alert-field-bg", fieldBackground);
			popup.style.setProperty("--satos-alert-field-border", fieldBorder);
			popup.style.setProperty(
				"--satos-alert-field-border-hover",
				fieldBorderHover,
			);
			popup.style.setProperty("--satos-alert-focus-color", focusColor);
			popup.style.setProperty("--satos-alert-focus-ring", focusRing);
			popup.style.setProperty("--satos-alert-footer-bg", footerBackground);
			popup.style.setProperty(
				"--satos-alert-validation-bg",
				validationBackground,
			);
			popup.style.setProperty(
				"--satos-alert-validation-color",
				validationColor,
			);
			popup.style.setProperty(
				"--satos-alert-cancel-text",
				theme.scheme === "dark" ? "#CBD5E1" : "#334155",
			);
			previousDidOpen?.(popup);
		},
	};
}
